import { supabase } from './client'
import { db } from '../db'
import type { AvatarCategory, Avatar } from '../db/types'
import { getCdnImageUrl, pickCdnSize, IS_WEB_BUILD } from '../cdn'

/**
 * Avatar API Service
 * Handles fetching and caching of avatar categories and presets
 * Supports both online (Supabase) and offline (Dexie) modes
 */

// ==================== Catalog Versioning ====================

/**
 * localStorage key holding the last successfully-synced server version of
 * the shared avatar catalog. Survives the logout wipe (not in
 * USER_SCOPED_KEYS) so we can short-circuit catalog re-fetches across
 * sessions.
 */
const CATALOG_VERSION_KEY = 'avatarCatalogVersion'

/**
 * Read the current avatar catalog version from `shared_catalog_versions`
 * (bumped by Supabase triggers on profile_avatars + categories).
 * Returns `null` on network/RLS failure so callers can fall back to the
 * local cache instead of erroring out.
 */
export async function fetchAvatarCatalogVersion(): Promise<number | null> {
  if (!supabase) return null
  try {
    const { data, error } = await supabase
      .from('shared_catalog_versions')
      .select('version')
      .eq('key', 'avatars')
      .maybeSingle()
    if (error || !data) return null
    return Number(data.version)
  } catch {
    return null
  }
}

async function isCatalogStale(): Promise<{ stale: boolean; remoteVersion: number | null }> {
  const remoteVersion = await fetchAvatarCatalogVersion()
  if (remoteVersion === null) return { stale: false, remoteVersion: null }
  const local = Number(localStorage.getItem(CATALOG_VERSION_KEY) ?? '0')
  return { stale: remoteVersion !== local, remoteVersion }
}

function persistCatalogVersion(remoteVersion: number | null): void {
  if (remoteVersion === null) return
  try {
    localStorage.setItem(CATALOG_VERSION_KEY, String(remoteVersion))
  } catch {
    /* non-fatal — next sync will retry */
  }
}

// ==================== Supabase Queries ====================

/**
 * Fetch all avatar categories from Supabase
 * @returns Array of avatar categories sorted by sort_order
 */
export async function fetchAvatarCategories(): Promise<AvatarCategory[]> {
  if (!supabase) {
    throw new Error('Supabase client not available')
  }

  const { data, error } = await supabase
    .from('profile_avatarpresets_categories')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })

  if (error) throw error
  return data || []
}

/**
 * Fetch avatars by category ID from Supabase
 * @param categoryId - UUID of the category
 * @returns Array of avatars in the specified category
 */
export async function fetchAvatarsByCategory(categoryId: string): Promise<Avatar[]> {
  if (!supabase) {
    throw new Error('Supabase client not available')
  }

  const { data, error } = await supabase
    .from('profile_avatars')
    .select('*')
    .eq('category_id', categoryId)
    .eq('is_active', true)
    .eq('is_preset', true)

  if (error) throw error
  return data || []
}

/**
 * Fetch all avatars from Supabase
 * @returns Array of all avatars
 */
export async function fetchAllAvatars(): Promise<Avatar[]> {
  if (!supabase) {
    throw new Error('Supabase client not available')
  }

  const { data, error } = await supabase
    .from('profile_avatars')
    .select('*')
    .eq('is_active', true)
    .eq('is_preset', true)
    .order('category_id, id')

  if (error) throw error
  return data || []
}

// ==================== Supabase Storage ====================

/**
 * Get avatar image URL.
 *
 * - Web: always returns a direct Bunny CDN URL. The browser's HTTP cache +
 *   the Pull-Zone's Cache-Control headers handle re-renders, no IndexedDB
 *   detour. This also lets us request the correct size variant per call
 *   (64/128/256/512) instead of pinning everything to the 256 blob.
 * - Electron: prefers the Dexie cache blob for offline-first behavior;
 *   falls back to the CDN URL when no cache is present.
 *
 * Legacy `http(s)://…` file_path values (pre-Bunny-migration rows) are
 * passed through unchanged by `getCdnImageUrl` as a safety net.
 *
 * @param avatar Avatar object or a raw file_path / full URL string.
 * @param displaySize Logical CSS size in px the image will be rendered at.
 *                    Retina buffer is applied internally; caller just passes
 *                    the on-screen size. Defaults to 96 (picker grid hover).
 * @returns Renderable URL, or `null` if no path / no CDN base URL configured.
 */
export function getAvatarImageUrl(
  avatar: Avatar | string,
  displaySize: number = 96
): string | null {
  const filePath = typeof avatar === 'string' ? avatar : avatar.file_path

  if (IS_WEB_BUILD) {
    return getCdnImageUrl(filePath, pickCdnSize(displaySize * 2))
  }

  // Electron: offline-cache hit wins.
  if (typeof avatar !== 'string' && avatar.cached_image_blob) {
    return avatar.cached_image_blob
  }
  return getCdnImageUrl(filePath, pickCdnSize(displaySize * 2))
}

// ==================== User Profile Updates ====================

interface ProfileAvatarRow {
  id: string
  avatar_preset_id: string | null
  updated_at: string
  created_at?: string
}

/**
 * Update user's selected avatar preset in profiles table
 * @param userId - UUID of the user
 * @param avatarPresetId - UUID of the selected avatar preset
 * @returns Updated profile data
 */
export async function updateUserAvatarPreset(
  userId: string,
  avatarPresetId: string
): Promise<ProfileAvatarRow[]> {
  if (!supabase) {
    throw new Error('Supabase client not available')
  }

  // Check if profile exists. PGRST116 = "no rows" from .single() — that's the
  // INSERT branch below; any other error must propagate so we don't fall
  // through to INSERT and 409 on a PK collision after a transient RLS hiccup.
  const { data: existingProfile, error: lookupError } = await supabase
    .from('profiles')
    .select('id')
    .eq('id', userId)
    .single()

  if (lookupError && lookupError.code !== 'PGRST116') {
    throw lookupError
  }

  if (existingProfile) {
    // Profile exists - UPDATE
    const { data, error } = await supabase
      .from('profiles')
      .update({ avatar_preset_id: avatarPresetId, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()

    if (error) throw error
    return data
  } else {
    // Profile doesn't exist - INSERT
    const { data, error } = await supabase
      .from('profiles')
      .insert({
        id: userId,
        avatar_preset_id: avatarPresetId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()

    if (error) throw error
    return data
  }
}

// ==================== Image Caching ====================

/**
 * Download image from URL and convert to base64 data URL
 * @param imageUrl - Full URL to image
 * @returns Base64 data URL or null if failed
 */
async function downloadAndCacheImage(imageUrl: string): Promise<string | null> {
  try {
    const response = await fetch(imageUrl)
    if (!response.ok) return null

    const blob = await response.blob()

    // Convert blob to base64 data URL
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('[AvatarAPI] Failed to download image:', error)
    return null
  }
}

/**
 * Cache avatar images locally in Dexie
 * Downloads images from Supabase Storage and stores as base64
 * @param avatars - Array of avatars to cache
 */
async function cacheAvatarImages(avatars: Avatar[]): Promise<void> {

  // Download images in batches to avoid overwhelming the browser
  const BATCH_SIZE = 10
  const batches: Avatar[][] = []

  for (let i = 0; i < avatars.length; i += BATCH_SIZE) {
    batches.push(avatars.slice(i, i + BATCH_SIZE))
  }

  for (const batch of batches) {
    await Promise.all(
      batch.map(async (avatar) => {
        // Skip if already cached recently (within 7 days)
        if (avatar.cached_at) {
          const daysSinceCached = (Date.now() - new Date(avatar.cached_at).getTime()) / (1000 * 60 * 60 * 24)
          if (daysSinceCached < 7) {
            return // Skip, still fresh
          }
        }

        // Build CDN URL from the relative file_path. 256px covers every
        // place the same avatar is rendered in a session (picker hover,
        // profile picture). Legacy http(s) URLs pass through unchanged.
        const imageUrl = getCdnImageUrl(avatar.file_path, 256)
        if (!imageUrl) {
          return
        }
        const cachedData = await downloadAndCacheImage(imageUrl)

        if (cachedData) {
          await db.avatars.update(avatar.id, {
            cached_image_blob: cachedData,
            cached_at: new Date()
          })
        }
      })
    )
  }

}

// ==================== Offline Sync ====================

/**
 * Load avatar categories from local database (offline mode)
 * @returns Array of avatar categories
 */
export async function loadCategoriesFromLocal(): Promise<AvatarCategory[]> {
  // Filter in memory rather than via Dexie's `where('is_active').equals(true)`
  // — boolean values as IndexedDB keys are unreliable across browsers and
  // throw DexieError on some schema/data combinations. Mirrors the pattern
  // used in loadAvatarsFromLocal.
  const all = await db.avatarCategories.toArray()
  return all
    .filter((c) => c.is_active)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
}

/**
 * Load avatars from local database (offline mode)
 * @param categoryId - Optional category ID to filter by
 * @returns Array of avatars
 */
export async function loadAvatarsFromLocal(categoryId?: string): Promise<Avatar[]> {
  // Load all avatars and filter in memory (simpler than compound indexes)
  const allAvatars = await db.avatars.toArray()

  return allAvatars.filter(avatar => {
    // Must be active and preset
    if (!avatar.is_active || !avatar.is_preset) {
      return false
    }

    // If categoryId specified, must match
    if (categoryId && avatar.category_id !== categoryId) {
      return false
    }

    return true
  })
}

// ==================== Smart Loading (Hybrid) ====================

/**
 * Reconcile the local avatar catalog (categories + avatars) with Supabase if
 * the server version differs from the locally-persisted version. Performs
 * orphan deletion (rows present locally but missing remotely), `file_path`-
 * aware blob invalidation, and TTL-based image re-caching. Persists the new
 * version on success — next call is a single SELECT until the server bumps.
 *
 * Both tables sync together because they share a single version key
 * ('avatars'). The version is only persisted after BOTH sub-syncs succeed,
 * so a failed mid-flight category sync won't desync the avatars next time.
 *
 * @param options.force Skip the version check and always pull. Required for
 *   cold-start (empty cache) so we don't get stuck with an empty picker
 *   when the version query fails or the seed row is missing.
 */
async function syncAvatarCatalog(options: { force?: boolean } = {}): Promise<void> {
  // A fresh BrowserWindow (e.g. the standalone Electron preferences window)
  // creates its own Supabase client that hydrates the persisted session
  // asynchronously from `safeStorage`. Without awaiting the session, the
  // catalog SELECTs go out anonymously and the RLS-protected
  // `profile_avatars` / `profile_avatarpresets_categories` tables return
  // empty, leaving the picker visually empty until manual refresh.
  if (supabase) {
    try { await supabase.auth.getSession() } catch { /* non-fatal */ }
  }

  let remoteVersion: number | null
  if (options.force) {
    remoteVersion = await fetchAvatarCatalogVersion()
  } else {
    const staleness = await isCatalogStale()
    if (!staleness.stale) return
    remoteVersion = staleness.remoteVersion
  }

  // Categories first — their orphan removal is independent of avatars.
  let categoriesOk = false
  try {
    const freshCategories = await fetchAvatarCategories()
    const existingCategories = await db.avatarCategories.toArray()
    const freshIds = new Set(freshCategories.map((c) => c.id))
    const orphanIds = existingCategories.filter((c) => !freshIds.has(c.id)).map((c) => c.id)
    if (orphanIds.length) await db.avatarCategories.bulkDelete(orphanIds)
    await db.avatarCategories.bulkPut(freshCategories)
    categoriesOk = true
  } catch (err) {
    console.error('[AvatarAPI] Category sync failed:', err)
  }

  // Avatars — preserves cache fields except when file_path changed (the path
  // is the source of truth for which Bunny CDN asset is rendered, so a path
  // change must invalidate the cached blob immediately rather than waiting
  // for the 7-day TTL). On Web the cache fields are unconditionally stripped
  // — there's no offline path, the browser HTTP cache + the Bunny Pull-Zone
  // headers are the real cache, and base64 blobs would just burn IndexedDB
  // quota. Stripping here also cleans up rows left over from earlier builds
  // that did populate the blob in Web.
  let avatarsOk = false
  try {
    const freshAvatars = await fetchAllAvatars()
    const existingAvatars = await db.avatars.toArray()
    const existingMap = new Map(existingAvatars.map((a) => [a.id, a]))
    const freshIds = new Set(freshAvatars.map((a) => a.id))
    const orphanIds = existingAvatars.filter((a) => !freshIds.has(a.id)).map((a) => a.id)
    if (orphanIds.length) await db.avatars.bulkDelete(orphanIds)

    let needsCaching = false
    const mergedAvatars = freshAvatars.map((fresh) => {
      if (IS_WEB_BUILD) {
        // Web never caches blobs — return the fresh row with cache fields
        // explicitly nulled. If the existing row still carries blob data
        // from a pre-refactor build, this overwrites it.
        return { ...fresh, cached_image_blob: null, cached_at: null }
      }
      const existing = existingMap.get(fresh.id)
      if (!existing) {
        needsCaching = true
        return fresh
      }
      const filePathChanged = existing.file_path !== fresh.file_path
      if (filePathChanged || !existing.cached_at) {
        needsCaching = true
      } else {
        const daysSinceCached = (Date.now() - new Date(existing.cached_at).getTime()) / (1000 * 60 * 60 * 24)
        if (daysSinceCached >= 7) needsCaching = true
      }
      return {
        ...fresh,
        cached_image_blob: filePathChanged ? null : existing.cached_image_blob,
        cached_at: filePathChanged ? null : existing.cached_at,
      }
    })

    await db.avatars.bulkPut(mergedAvatars)
    if (needsCaching && !IS_WEB_BUILD) {
      cacheAvatarImages(mergedAvatars).catch((err) =>
        console.error('[AvatarAPI] Image caching failed:', err)
      )
    }
    avatarsOk = true
  } catch (err) {
    console.error('[AvatarAPI] Avatar sync failed:', err)
  }

  if (categoriesOk && avatarsOk) {
    persistCatalogVersion(remoteVersion)
  }
}

/**
 * Force a full catalog re-sync regardless of the version counter. Useful
 * for a future "Reload catalog" affordance and for debugging stale data.
 * Currently exported but not wired into any UI.
 */
export async function refreshAvatarCatalog(): Promise<void> {
  try {
    localStorage.removeItem(CATALOG_VERSION_KEY)
  } catch {
    /* non-fatal */
  }
  await syncAvatarCatalog({ force: true })
}

/**
 * Load avatar categories with automatic online/offline detection.
 * Cache-first: returns local rows immediately and triggers a background
 * version-gated sync. On cold start (empty cache) it awaits the sync
 * before returning so the picker has data on first open.
 */
export async function loadAvatarCategories(): Promise<AvatarCategory[]> {
  const cached = await loadCategoriesFromLocal()
  if (cached.length > 0) {
    if (supabase) {
      syncAvatarCatalog().catch((err) =>
        console.error('[AvatarAPI] Background catalog sync failed:', err)
      )
    }
    return cached
  }

  if (supabase) {
    // Cold start: bypass the version gate. We have nothing to compare
    // against, and a null version response would otherwise short-circuit
    // the sync and leave the picker empty.
    await syncAvatarCatalog({ force: true })
    return await loadCategoriesFromLocal()
  }
  return []
}

/**
 * Load avatars with automatic online/offline detection.
 * Cache-first: returns local rows immediately and triggers a background
 * version-gated sync. On cold start (empty cache) it awaits the sync
 * before returning.
 */
export async function loadAvatars(): Promise<Avatar[]> {
  const cached = await loadAvatarsFromLocal()
  if (cached.length > 0) {
    if (supabase) {
      syncAvatarCatalog().catch((err) =>
        console.error('[AvatarAPI] Background catalog sync failed:', err)
      )
    }
    return cached
  }

  if (supabase) {
    // Cold start: see loadAvatarCategories for why we force.
    await syncAvatarCatalog({ force: true })
    return await loadAvatarsFromLocal()
  }
  return []
}

// ==================== Search & Filter ====================

/**
 * Search avatars by name or tags
 * @param query - Search query string
 * @param language - Language for tags ('de' or 'en')
 * @returns Array of matching avatars
 */
export async function searchAvatars(query: string, language: 'de' | 'en' = 'de'): Promise<Avatar[]> {
  const avatars = await loadAvatars()
  const lowerQuery = query.toLowerCase()

  return avatars.filter((avatar) => {
    // Search in name
    if (avatar.name.toLowerCase().includes(lowerQuery)) {
      return true
    }

    // Search in tags
    const tags = language === 'de' ? avatar.tags_de : avatar.tags_en
    if (tags && tags.some((tag) => tag.toLowerCase().includes(lowerQuery))) {
      return true
    }

    return false
  })
}
